// src/components/buttons/HomeRefreshButton.jsx
import React from 'react';
import { Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { Map, Public } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const HomeRefreshButton = ({ 
  label = "Към интерактивната карта",
  icon = <Map />,
  fullWidth = false,
  centered = true,
  size = "large",
  variant = "contained",
  color = "primary",
  onClick,
  to = "/",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleHomeClick = (e) => {
    if (onClick) {
      onClick(e);
    }
    
    // Ако сме вече на главната страница, рефрешваме
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.location.reload();
    }
  };

  // Определяне на размера според пропса или мобилността
  const buttonSize = size || (isMobile ? "medium" : "large");
  
  // Определяне на стилове според варианта
  const getButtonStyles = () => {
    const baseStyles = {
      borderRadius: 2,
      fontWeight: 600,
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      width: fullWidth ? '100%' : 'auto',
      '&:hover': {
        transform: 'translateY(-2px)'
      }
    };

    switch (variant) {
      case 'contained':
        return {
          ...baseStyles,
          background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
          '&:hover': {
            ...baseStyles['&:hover'],
            background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)'
          }
        };
      
      case 'outlined':
        const colorMap = {
          primary: { border: '#1976d2', text: '#1976d2', hoverBg: 'rgba(25, 118, 210, 0.04)' },
          secondary: { border: '#9c27b0', text: '#9c27b0', hoverBg: 'rgba(156, 39, 176, 0.04)' },
          warning: { border: '#ed6c02', text: '#ed6c02', hoverBg: 'rgba(237, 108, 2, 0.04)' },
          success: { border: '#2e7d32', text: '#2e7d32', hoverBg: 'rgba(46, 125, 50, 0.04)' }
        };
        
        const colors = colorMap[color] || colorMap.primary;
        
        return {
          ...baseStyles,
          borderColor: colors.border,
          color: colors.text,
          '&:hover': {
            ...baseStyles['&:hover'],
            borderColor: colors.border,
            background: colors.hoverBg
          }
        };
      
      default:
        return baseStyles;
    }
  };

  // Определяне на padding според размера
  const getPadding = () => {
    switch (buttonSize) {
      case 'small':
        return { xs: '6px 16px', md: '8px 20px' };
      case 'medium':
        return { xs: '8px 20px', md: '12px 24px' };
      case 'large':
        return { xs: '12px 24px', md: '14px 32px' };
      default:
        return { xs: '12px 24px', md: '14px 32px' };
    }
  };

  // Определяне на font size според размера
  const getFontSize = () => {
    switch (buttonSize) {
      case 'small':
        return { xs: '0.75rem', md: '0.8125rem' };
      case 'medium':
        return { xs: '0.8125rem', md: '0.875rem' };
      case 'large':
        return { xs: '0.875rem', md: '1rem' };
      default:
        return { xs: '0.875rem', md: '1rem' };
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: centered ? 'center' : 'flex-start',
      width: fullWidth ? '100%' : 'auto'
    }}>
      <Button
        component={Link}
        to={to}
        onClick={handleHomeClick}
        variant={variant}
        size={buttonSize}
        startIcon={React.cloneElement(icon, {
          sx: { fontSize: buttonSize === 'small' ? '18px' : '20px' }
        })}
        sx={{
          px: getPadding().xs,
          py: buttonSize === 'small' ? 0.5 : 1,
          fontSize: getFontSize().xs,
          [theme.breakpoints.up('md')]: {
            px: getPadding().md,
            py: buttonSize === 'small' ? 0.75 : 1.25,
            fontSize: getFontSize().md
          },
          ...getButtonStyles()
        }}
      >
        {label}
      </Button>
    </Box>
  );
};

export default HomeRefreshButton;